import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  RefreshCw, Search, ChevronDown, CheckCircle2, XCircle, Loader2,
  AlertCircle, Upload, X, ArrowUpRight, ArrowDownRight, Clock,
  Pause, Play, Square, MinusCircle
} from 'lucide-react';

/**
 * Update Brand (V2).
 *
 * The V1 page held one long HTTP request open and read progress off an SSE
 * stream: if the gateway timed out, or the tab was closed, or the laptop
 * slept, the run was gone with nothing to return to. Here the request only
 * starts a job — the pipeline runs on the server and this page polls
 * /api/updatebrandv2/status, so progress survives a reload and several brands
 * can be updated one after another without babysitting the tab.
 *
 * V1 is still routed at /update-brand-v1 (it is the only path that takes a
 * SEMrush CSV export as its source of truth).
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const CSV_UPLOAD_ALLOWED_EMAIL = 'mars.mediacle@gmail.com';
const POLL_MS = 3000;

// Fallback only — /status returns the authoritative list with the same keys.
const DEFAULT_STAGES = [
  { key: 'collect', label: 'Fetching referral websites' },
  { key: 'classify', label: 'Scanning websites for affiliates' },
  { key: 'enrich', label: 'Gathering affiliate insights' },
  { key: 'verify', label: 'Checking affiliates still promote you' }
];

// Same shape the add-brand modal sends: whatever columns the CSV has, keyed by
// lower-cased header. The server reads domain / backlinks_num / ip / country.
const parseCSV = (text) => {
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  const separator = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(separator).map(h => h.trim().toLowerCase().replace(/^"|"$/g, ''));
  const domainIndex = headers.findIndex(h => h === 'domain' || h === 'referring domain' || h === 'url');
  if (domainIndex === -1) return [];

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(separator);
    if (!values[domainIndex] || !values[domainIndex].trim()) continue;
    const row = {};
    headers.forEach((header, idx) => { row[header] = values[idx]?.trim() || ''; });
    rows.push(row);
  }
  return rows;
};

const formatElapsed = (fromIso, toIso) => {
  if (!fromIso) return '';
  const start = new Date(fromIso).getTime();
  const end = toIso ? new Date(toIso).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((end - start) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const UpdateBrandV2 = () => {
  const { user } = useSelector(state => state.auth || {});
  const canUploadCsv = user?.email === CSV_UPLOAD_ALLOWED_EMAIL;

  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);

  const [starting, setStarting] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');

  const [jobs, setJobs] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  // Re-render once a second so "running for 4m 12s" actually counts up.
  const [, setTick] = useState(0);

  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchBrands = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/fetchBrand`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const result = await response.json();
      if (result.success && Array.isArray(result.brands)) {
        setBrands(result.brands);
      }
    } catch (err) {
      console.error('Failed to load brands:', err);
    } finally {
      setLoadingBrands(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/updatebrandv2/status`, {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        setJobs(result.jobs || []);
        if (Array.isArray(result.stages) && result.stages.length) setStages(result.stages);
      }
    } catch (err) {
      // A failed poll is not worth showing — the next one is 3 seconds away.
      console.error('Update status poll failed:', err);
    }
  }, []);

  useEffect(() => {
    fetchBrands();
    fetchStatus();
    const poll = setInterval(fetchStatus, POLL_MS);
    const clock = setInterval(() => setTick(t => t + 1), 1000);
    return () => { clearInterval(poll); clearInterval(clock); };
  }, [fetchBrands, fetchStatus]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands.slice(0, 200);
    return brands
      .filter(b => (b.brand_url || '').toLowerCase().includes(q) || (b.brand_name || '').toLowerCase().includes(q))
      .slice(0, 200);
  }, [brands, search]);

  // A paused job is still in progress: it owns the brand's update and is
  // waiting to be told to carry on, so it belongs in the same list as a running
  // one — and the brand cannot be started again while it sits there.
  const inProgressJobs = jobs.filter(j => j.status === 'running' || j.status === 'paused');
  const finishedJobs = jobs.filter(j => j.status !== 'running' && j.status !== 'paused');
  const selectedIsRunning = selectedBrand
    && inProgressJobs.some(j => j.brand_url === selectedBrand.brand_url);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFormError('Please choose a .csv file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(e.target.result);
      if (parsed.length === 0) {
        setFormError('Could not read that CSV — it needs a header row with a "Domain" column.');
        return;
      }
      setFormError('');
      setCsvFile(file);
      setCsvData(parsed);
    };
    reader.readAsText(file);
  };

  const clearCsv = () => {
    setCsvFile(null);
    setCsvData([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleStart = async () => {
    if (!selectedBrand) {
      setFormError('Pick a brand first');
      return;
    }
    setStarting(true);
    setFormError('');
    setNotice('');

    try {
      const body = { brandUrl: selectedBrand.brand_url };
      if (canUploadCsv && csvData.length > 0) {
        body.csvBacklinks = csvData;
        body.skipSemrush = true;
      }

      const response = await fetch(`${API_URL}/api/updatebrandv2/start`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const result = await response.json();

      if (!result.success) {
        // alreadyRunning is not an error — the run it refers to is shown below.
        if (result.alreadyRunning) setNotice(result.message);
        else setFormError(result.message || 'Could not start the update');
      } else {
        setNotice(`${result.brand?.brand_name || selectedBrand.brand_name} is updating. You can leave this page — it keeps running.`);
        clearCsv();
      }
      fetchStatus();
    } catch (err) {
      setFormError(err.message || 'Could not start the update');
    } finally {
      setStarting(false);
    }
  };

  // Pause / Resume / Stop. The job id is tracked so only the button that was
  // pressed shows a spinner, and the status is re-polled straight away rather
  // than waiting up to three seconds for the card to catch up.
  const [busyJobId, setBusyJobId] = useState(null);
  const [confirmStopId, setConfirmStopId] = useState(null);

  const controlJob = useCallback(async (jobId, action) => {
    setBusyJobId(jobId);
    setFormError('');
    try {
      const response = await fetch(`${API_URL}/api/updatebrandv2/${action}/${jobId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const result = await response.json();
      if (result.success) setNotice(result.message);
      else setFormError(result.message || `Could not ${action} that update`);
      await fetchStatus();
    } catch (err) {
      setFormError(err.message || `Could not ${action} that update`);
    } finally {
      setBusyJobId(null);
      setConfirmStopId(null);
    }
  }, [fetchStatus]);

  const stageIndex = (key) => stages.findIndex(s => s.key === key);

  const renderJob = (job) => {
    const detail = job.detail || {};
    const running = job.status === 'running';
    const paused = job.status === 'paused';
    const stopped = job.status === 'cancelled';
    const failed = job.status === 'failed';
    const busy = busyJobId === job.id;

    // A paused run has not reached the stage after the one it stopped in, so
    // the stage list should show it as held there rather than completed.
    const currentStage = stageIndex(job.stage);
    const inProgress = running || paused;

    return (
      <div
        key={job.id}
        className="p-5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {running && <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />}
              {paused && <Pause className="w-4 h-4 text-amber-500 flex-shrink-0" />}
              {stopped && <MinusCircle className="w-4 h-4 text-gray-400 flex-shrink-0" />}
              {!inProgress && !stopped && !failed && <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
              {failed && <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
              <span className="font-medium text-gray-900 dark:text-white truncate">
                {job.brand_name || job.brand_url}
              </span>
              <span className="text-xs text-gray-400 dark:text-slate-500 truncate">{job.brand_url}</span>
            </div>
            <div className="mt-1 text-sm text-gray-600 dark:text-slate-400">
              {failed ? (job.error || 'Update failed') : job.stage_label}
            </div>
            {paused && (
              <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                Paused — nothing is being spent. Resume picks up from the exact point it stopped.
              </div>
            )}
            {stopped && (
              <div className="mt-1 text-xs text-gray-500 dark:text-slate-500">
                Stopped before it finished. Everything it had found by then was kept.
              </div>
            )}
            {/* A run that outlived a server restart. Without this the elapsed
                time and the counters look inexplicable — the job picked up
                where it stopped rather than starting over, and that is worth
                saying rather than leaving someone to wonder. */}
            {job.attempts > 1 && !failed && (
              <div className="mt-1 text-xs text-gray-500 dark:text-slate-500">
                Continued after a server restart — it kept everything it had already checked.
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              {/* A paused run is not accruing time — freeze the clock at the
                  moment it was paused rather than counting up while nothing
                  happens. */}
              {formatElapsed(job.started_at, paused ? job.updated_at : job.finished_at)}
            </div>

            {inProgress && (
              <div className="flex items-center gap-1">
                {running && (
                  <JobButton
                    onClick={() => controlJob(job.id, 'pause')}
                    disabled={busy}
                    busy={busy}
                    icon={Pause}
                    title="Pause — stops spending, keeps everything checked so far"
                  >
                    Pause
                  </JobButton>
                )}
                {paused && (
                  <JobButton
                    onClick={() => controlJob(job.id, 'resume')}
                    disabled={busy}
                    busy={busy}
                    icon={Play}
                    tone="primary"
                    title="Carry on from where it stopped"
                  >
                    Resume
                  </JobButton>
                )}
                {confirmStopId === job.id ? (
                  <>
                    <JobButton
                      onClick={() => controlJob(job.id, 'stop')}
                      disabled={busy}
                      busy={busy}
                      icon={Square}
                      tone="danger"
                    >
                      Stop for good
                    </JobButton>
                    <JobButton onClick={() => setConfirmStopId(null)}>Cancel</JobButton>
                  </>
                ) : (
                  <JobButton
                    onClick={() => setConfirmStopId(job.id)}
                    disabled={busy}
                    icon={Square}
                    title="End this update — it cannot be resumed afterwards"
                  >
                    Stop
                  </JobButton>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-400 mb-1">
            <span>
              {running ? 'Running' : paused ? 'Paused' : stopped ? 'Stopped' : failed ? 'Failed' : 'Done'}
            </span>
            <span>{job.percent}%</span>
          </div>
          <div className="h-2 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                failed ? 'bg-red-500'
                  : paused ? 'bg-amber-500'
                    : stopped ? 'bg-gray-400'
                      : running ? 'bg-blue-500'
                        : 'bg-emerald-500'
              }`}
              style={{ width: `${Math.max(2, job.percent || 0)}%` }}
            />
          </div>
        </div>

        {/* Stage list — where the run is, and what it has already been through */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {stages.map((stage, idx) => {
            // A run that stopped part way through has not done the stages it
            // never reached, so only a run that actually finished marks them all.
            const done = !failed && !stopped && (currentStage > idx || !inProgress);
            const active = inProgress && currentStage === idx;
            return (
              <div
                key={stage.key}
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  active
                    ? paused
                      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                      : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : done
                      ? 'text-gray-600 dark:text-slate-400'
                      : 'text-gray-400 dark:text-slate-600'
                }`}
              >
                {active
                  ? paused
                    ? <Pause className="w-4 h-4 flex-shrink-0" />
                    : <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  : done
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    : <div className="w-4 h-4 rounded-full border border-current flex-shrink-0" />}
                <span className="truncate">{stage.label}</span>
              </div>
            );
          })}
        </div>

        {/* Counters straight from the job's detail column */}
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {detail.domains != null && <Counter label="referral websites" value={detail.domains} />}
          {detail.classified != null && detail.total != null && job.stage === 'classify' && (
            <Counter label="scanned" value={`${detail.classified}/${detail.total}`} />
          )}
          {detail.affiliates != null && <Counter label="affiliates found" value={detail.affiliates} />}
          {detail.checked != null && detail.total != null && job.stage === 'verify' && (
            <Counter label="checked" value={`${detail.checked}/${detail.total}`} />
          )}
          {detail.gained != null && (
            <Counter label="new" value={detail.gained} tone="up" icon={ArrowUpRight} />
          )}
          {detail.lost != null && (
            <Counter label="dropped you" value={detail.lost} tone="down" icon={ArrowDownRight} />
          )}
          {detail.stillLive != null && <Counter label="still live" value={detail.stillLive} />}
          {detail.uncheckable > 0 && (
            <Counter label="could not be checked (kept)" value={detail.uncheckable} />
          )}
        </div>

        {/* Google blocks the scraping pool from time to time, and while it does
            nothing can be verified. Those affiliates are kept and left as they
            were — but without this the run looks like it checked them and found
            no page, which is the opposite of what happened. */}
        {detail.searchUnavailable && (
          <div className="mt-3 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            Google search was unavailable during this run, so the affiliates above could not be
            re-checked. They have been kept exactly as they were — run the update again later to
            verify them.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Update Brand</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
          Re-runs a brand that is already in the system: finds affiliates that have started
          linking to it, and re-checks the ones it already has so any that stopped promoting
          it are dropped. It runs on the server — you can close this page and come back.
        </p>
      </div>

      <div className="p-5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
          Brand
        </label>

        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-left"
          >
            <span className={selectedBrand ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-slate-500'}>
              {loadingBrands
                ? 'Loading brands…'
                : selectedBrand
                  ? `${selectedBrand.brand_name || selectedBrand.brand_url} — ${selectedBrand.brand_url}`
                  : 'Select a brand'}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </button>

          {dropdownOpen && (
            <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg">
              <div className="p-2 border-b border-gray-100 dark:border-slate-800">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search brands"
                    className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {filteredBrands.length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400">No brands match that.</div>
                )}
                {filteredBrands.map((brand) => (
                  <button
                    key={brand.brand_url}
                    type="button"
                    onClick={() => { setSelectedBrand(brand); setDropdownOpen(false); setSearch(''); }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-slate-800"
                  >
                    <div className="text-sm text-gray-900 dark:text-white">{brand.brand_name || brand.brand_url}</div>
                    <div className="text-xs text-gray-400 dark:text-slate-500">{brand.brand_url}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {canUploadCsv && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Backlinks CSV <span className="font-normal text-gray-400 dark:text-slate-500">(optional — skips SEMrush)</span>
            </label>
            {csvFile ? (
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg">
                <span className="text-sm text-gray-700 dark:text-slate-300 truncate">
                  {csvFile.name} — {csvData.length} referring domains
                </span>
                <button type="button" onClick={clearCsv} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-slate-800 border border-dashed border-gray-300 dark:border-slate-600 rounded-lg cursor-pointer text-sm text-gray-500 dark:text-slate-400">
                <Upload className="w-4 h-4" />
                Choose a CSV
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" />
              </label>
            )}
          </div>
        )}

        {formError && (
          <div className="mt-4 flex items-start gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {formError}
          </div>
        )}
        {notice && !formError && (
          <div className="mt-4 flex items-start gap-2 text-sm text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {notice}
          </div>
        )}

        <button
          type="button"
          onClick={handleStart}
          disabled={starting || !selectedBrand || selectedIsRunning}
          className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {selectedIsRunning ? 'Already in progress' : starting ? 'Starting…' : 'Update brand'}
        </button>
      </div>

      {inProgressJobs.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">In progress</h2>
          <div className="space-y-3">{inProgressJobs.map(renderJob)}</div>
        </div>
      )}

      {finishedJobs.length > 0 && (
        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">Recently finished</h2>
          <div className="space-y-3">{finishedJobs.map(renderJob)}</div>
        </div>
      )}

      {inProgressJobs.length === 0 && finishedJobs.length === 0 && (
        <p className="mt-6 text-sm text-gray-500 dark:text-slate-400">
          No updates running. Anything you start here will show up in this list, including
          after a reload.
        </p>
      )}
    </div>
  );
};

// Small control on a job card. Deliberately quiet: these sit next to the
// elapsed time, not in the page's main flow.
const JobButton = ({ onClick, disabled, busy, icon: Icon, tone, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      tone === 'primary'
        ? 'bg-blue-600 hover:bg-blue-700 border-blue-600 text-white'
        : tone === 'danger'
          ? 'bg-red-600 hover:bg-red-700 border-red-600 text-white'
          : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
    }`}
  >
    {busy
      ? <Loader2 className="w-3 h-3 animate-spin" />
      : Icon && <Icon className="w-3 h-3" />}
    {children}
  </button>
);

const Counter = ({ label, value, tone, icon: Icon }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full ${
      tone === 'up'
        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
        : tone === 'down'
          ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
          : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400'
    }`}
  >
    {Icon && <Icon className="w-3 h-3" />}
    <span className="font-medium">{value}</span> {label}
  </span>
);

export default UpdateBrandV2;
